import React, { useCallback } from 'react';
import { Col, Input, InputNumber, Row, Select, Typography } from 'components';
import { Form, FormInstance } from 'antd';
import { ValidationErrors } from 'constants/validations';
import { PAYMENT_TERMS } from 'staticData/form';
import { preventNonNumericValue } from 'utils/helpers';
import { PercentageIcon } from 'components/common/icons';
import { useWatch } from 'antd/es/form/Form';
import { usePaymentMethodQuery } from 'api';

const { Text } = Typography;
const { Item } = Form;

const PAYMENT_METHODS = ['Swift', 'Wire', 'Check'];
const CURRENCY = [
  {
    value: 'USD',
    label: 'Usd/$',
  },
  {
    value: 'EUR',
    label: 'Eur/€',
  },
];

export const CURRENCY_MAPPING = {
  USD: 'Usd/$',
  EUR: 'Eur/€',
};

const getLabelById = (paymentMethodData: Record<string, any>[], id: string) => {
  return paymentMethodData.find((method: any) => method.id === id)?.label;
};

interface IPaymentTerms {
  onFinish: () => void;
  form: FormInstance;
  paymentTerms: Record<string, any>;
  isFinalStatus?: boolean;
}

export const PaymentTerms = React.memo((props: IPaymentTerms) => {
  const { form, paymentTerms, onFinish, isFinalStatus } = props;
  const paymentType = useWatch(['paymentTerms', 'paymentTerms'], form);
  const paymentMethod = useWatch(['paymentTerms', 'paymentMethodId'], form);
  const { data: paymentMethodData = [] } = usePaymentMethodQuery();

  const handlePrepaymentChange = useCallback(
    (value: number | null) => {
      if (value) {
        form.setFieldsValue({
          paymentTerms: {
            postPayment: Math.max(0, 100 - value).toFixed(2),
          },
        });
      } else {
        form.setFieldsValue({
          paymentTerms: {
            postPayment: '',
          },
        });
      }
    },
    [form]
  );

  return (
    <Row style={{ padding: '20px' }} gutter={[0, 20]}>
      <Col span={24}>
        <Text style={{ color: '#05796B' }} className={'font-weight-800 font-size-16'}>
          Payment Terms
        </Text>
      </Col>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              label={'Payment Terms*'}
              name={['paymentTerms', 'paymentTerms']}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Payment Terms'),
                },
              ]}
            >
              <Select
                disabled={isFinalStatus}
                size={'large'}
                options={PAYMENT_TERMS}
                onChange={(value) => {
                  form.setFieldsValue({
                    paymentTerms: {
                      paymentTerms: value,
                      threshold: undefined,
                      prepayment: undefined,
                      postPayment: undefined,
                      paymentMethodId: undefined,
                      netDaysAfterDelivery: undefined,
                      bankTransferFee: undefined,
                      ccPaymentProcessingFee: undefined,
                      currency: undefined,
                      conversionRate: undefined,
                    },
                  });
                  onFinish();
                }}
              />
            </Item>
          </Col>
          {paymentType !== 'PREPAID' && (
            <Col span={12}>
              <Item
                name={['paymentTerms', 'threshold']}
                label={'Threshold*'}
                rules={[
                  {
                    required: true,
                    message: ValidationErrors.required('Threshold'),
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      if (parseFloat(value) <= 0)
                        return Promise.reject(new Error('Threshold must be a positive number.'));
                      if (!/^\d+$/.test(value))
                        return Promise.reject(new Error('Threshold can only contain whole numbers, without decimals.'));

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  disabled={isFinalStatus}
                  controls={false}
                  size={'large'}
                  onBlur={onFinish}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                />
              </Item>
            </Col>
          )}
          {!(paymentType === 'PREPAID' || paymentType === 'POST_PAID') && (
            <Col span={12}>
              <Item
                name={['paymentTerms', 'prepayment']}
                label={'Prepayment*'}
                rules={[
                  {
                    required: true,
                    message: ValidationErrors.required('Prepayment'),
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const num = parseFloat(value);

                      if (isNaN(num)) return Promise.reject(new Error('Prepayment must be a valid number.'));
                      if (num <= 0) return Promise.reject(new Error('Prepayment must be a positive number.'));
                      if (num >= 100) return Promise.reject(new Error('Prepayment must be less than 100.'));
                      if (!/^\d+(\.\d{1,2})?$/.test(value))
                        return Promise.reject(new Error('Prepayment can have up to 2 decimal points.'));

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  disabled={isFinalStatus}
                  controls={false}
                  size={'large'}
                  onBlur={onFinish}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={<PercentageIcon />}
                  onChange={handlePrepaymentChange}
                />
              </Item>
            </Col>
          )}
          {!(paymentType === 'PREPAID' || paymentType === 'POST_PAID') && (
            <Col span={12}>
              <Item name={['paymentTerms', 'postPayment']} label={'Post-payment'}>
                <Input size={'large'} suffix={<PercentageIcon />} disabled={true} />
              </Item>
            </Col>
          )}
          <Col span={12}>
            <Item
              name={['paymentTerms', 'paymentMethodId']}
              label={'Payment Methods*'}
              rules={[
                {
                  required: true,
                  message: ValidationErrors.required('Payment Methods'),
                },
              ]}
            >
              <Select
                disabled={isFinalStatus}
                size={'large'}
                options={paymentMethodData}
                onChange={(value) => {
                  const label1 = PAYMENT_METHODS.includes(getLabelById(paymentMethodData, paymentMethod));
                  const label2 = PAYMENT_METHODS.includes(getLabelById(paymentMethodData, value));
                  if (label1 !== label2) {
                    form.setFieldsValue({
                      paymentTerms: {
                        paymentMethodId: value,
                        bankTransferFee: undefined,
                        ccPaymentProcessingFee: undefined,
                      },
                    });
                  }
                  onFinish();
                }}
              />
            </Item>
          </Col>
          {paymentType !== 'PREPAID' && (
            <Col span={12}>
              <Item
                name={['paymentTerms', 'netDaysAfterDelivery']}
                label={'Net Days After Delivery*'}
                rules={[
                  {
                    required: true,
                    message: ValidationErrors.required('Net Days After Delivery'),
                  },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      if (parseFloat(value) <= 0)
                        return Promise.reject(new Error('Net Days After Delivery must be a positive number.'));
                      if (!/^\d+$/.test(value))
                        return Promise.reject(
                          new Error('Net Days After Delivery can only contain whole numbers, without decimals.')
                        );

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  disabled={isFinalStatus}
                  controls={false}
                  size={'large'}
                  onBlur={onFinish}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={<Typography.Text type={'secondary'}>day</Typography.Text>}
                />
              </Item>
            </Col>
          )}
          {paymentMethod &&
            (PAYMENT_METHODS.includes(
              paymentMethodData.find((_paymentMethod: any) => _paymentMethod.id === paymentMethod)?.label
            ) ? (
              <Col span={12}>
                <Item
                  name={['paymentTerms', 'bankTransferFee']}
                  label={'Bank Transfer Fee'}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === '') {
                          return Promise.resolve();
                        }
                        const num = parseFloat(value);
                        if (isNaN(num)) {
                          return Promise.reject(new Error('Bank Transfer Fee must be a valid number.'));
                        }
                        if (num < 0) {
                          return Promise.reject(new Error('Bank Transfer Fee  must be a positive number.'));
                        }
                        if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                          return Promise.reject(new Error('Bank Transfer Fee  can have up to 2 decimal points.'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    disabled={isFinalStatus}
                    controls={false}
                    size={'large'}
                    onBlur={onFinish}
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                  />
                </Item>
              </Col>
            ) : (
              <Col span={12}>
                <Row align={'middle'} gutter={8} wrap={false}>
                  <Col span={18}>
                    <Item
                      name={['paymentTerms', 'ccPaymentProcessingFee']}
                      label={<Text className={'no-wrap'}>CC Payment Processing Fee</Text>}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === '') {
                              return Promise.resolve();
                            }
                            const num = parseFloat(value);
                            if (isNaN(num)) {
                              return Promise.reject(new Error('CC Payment Processing Fee must be a valid number.'));
                            }
                            if (num < 0) {
                              return Promise.reject(new Error('CC Payment Processing Fee must be a positive number.'));
                            }
                            if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                              return Promise.reject(
                                new Error('CC Payment Processing Fee can have up to 2 decimal points.')
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber<number>
                        disabled={isFinalStatus}
                        controls={false}
                        size={'large'}
                        onBlur={onFinish}
                        onBeforeInput={(event: any) => preventNonNumericValue(event)}
                        style={{ width: '100%' }}
                        suffix={<Typography.Text type={'secondary'}>%</Typography.Text>}
                      />
                    </Item>
                  </Col>
                  <Col>
                    <Item label={<span style={{ display: 'none' }}></span>}>
                      <Text className={'font-weight-600 font-size-16'}>= {paymentTerms.ccTransferFee}€</Text>
                    </Item>
                  </Col>
                </Row>
              </Col>
            ))}
          {paymentMethod && (
            <>
              <Col span={6}>
                <Item name={['paymentTerms', 'currency']} label={'Currency'}>
                  <Select disabled={isFinalStatus} options={CURRENCY} size={'large'} onChange={onFinish} />
                </Item>
              </Col>
              <Col span={6}>
                <Item
                  name={['paymentTerms', 'conversionRate']}
                  label={'Exchange Rate'}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === '') {
                          return Promise.resolve();
                        }
                        const num = parseFloat(value);
                        if (isNaN(num)) {
                          return Promise.reject(new Error('Conversion Rate must be a valid number.'));
                        }
                        if (num < 0) {
                          return Promise.reject(new Error('Conversion Rate must be a positive number.'));
                        }
                        if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                          return Promise.reject(new Error('Conversion Rate can have up to 2 decimal points.'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    disabled={isFinalStatus}
                    controls={false}
                    size={'large'}
                    onBlur={onFinish}
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                  />
                </Item>
              </Col>
            </>
          )}
        </Row>
      </Col>
    </Row>
  );
});
